import { AxiosResponse } from "axios";
import { client } from "./AxiosClient";
import {
  User,
  ResendEmail,
  Plan,
  INote,
  ISectionConfig,
  ITemplate,
  ITemplateVersion,
  Notification,
  ISharedTemplate,
} from "./Types";

interface LoginData {
  email: string;
  password: string;
}

interface Note {
  results: INote[];
  count: number;
}

interface Template {
  results: ITemplate[];
  count: number;
}

interface SharedTemplate {
  results: ISharedTemplate[];
  count: number;
}
interface TemplateVersion {
  results: ITemplateVersion[];
  count: number;
}

interface NotificationRes {
  results: Notification[];
  count: number;
}

interface ShareTemplate {
  message: string;
  pending_approvals: number;
  shared_with: string[];
}

interface UserProfile {
  // Define the properties of a user profile object.
}

export function registration(data: User): Promise<AxiosResponse> {
  return client.post("api/auth/registration/", data, {
    headers: { authorization: false },
  });
}

export function verifyEmail(data: string): Promise<AxiosResponse> {
  return client.get(`api/auth/registration/verify-email/${data}/`);
}

export function getPlan(params: { price_id: string }): Promise<AxiosResponse> {
  return client.get(`api/payments/price/?price_id=${params.price_id}`);
}

export function managePlan(): Promise<AxiosResponse> {
  return client.post(`api/payments/create-customer-portal-session/`);
}

export function cancelPlan(): Promise<AxiosResponse> {
  return client.post(`api/payments/cancel-subscription/`);
}

export function createNewNote(): Promise<AxiosResponse> {
  return client.post(`api/notes/`);
}

export function updateUserInfo(data: User): Promise<AxiosResponse<User>> {
  return client.patch(`api/user-profile/update/`, data);
}

export function updateUserPassword(data: User): Promise<AxiosResponse<User>> {
  return client.patch(`api/user-profile/update-password/`, data);
}

export function logOutOtherSessions(data: User): Promise<AxiosResponse<User>> {
  return client.post(`api/user-profile/logout-other-sessions/`, data);
}

export function deleteAccount(data: {
  password: string | undefined;
}): Promise<AxiosResponse> {
  return client.post(`api/user-profile/delete-account/`, { ...data });
}

export function login({ email, password }: LoginData): Promise<AxiosResponse> {
  return client.post(
    "api/login/",
    { email, password },
    { headers: { authorization: false } }
  );
}

export function getProfile(): Promise<AxiosResponse<UserProfile>> {
  return client.get("/users/profile");
}

export function getPlans(): Promise<AxiosResponse> {
  return client.get("api/payments/details/");
}

export function getCurrentPlan(): Promise<AxiosResponse<Plan>> {
  return client.get("api/payments/user-details/");
}

export function getClientSecret(): Promise<AxiosResponse> {
  return client.post("api/payments/initiate/");
}

export function renewSubscription(): Promise<AxiosResponse> {
  return client.post("api/payments/keep-up-subscription/");
}

export function checkout(data: {
  setup_intent_id: string;
  price_id: string;
}): Promise<AxiosResponse> {
  return client.post("api/payments/checkout-confirm/", data);
}

export function passwordReset(data: { email: string }): Promise<AxiosResponse> {
  return client.post("api/password-reset/", data);
}

export function validatePasswordToken(data: {
  token: string;
}): Promise<AxiosResponse> {
  return client.post("api/password-reset/validate_token/", data);
}

export function logout(data: { refresh: string }): Promise<AxiosResponse> {
  return client.post("api/logout/", data);
}

export function confirmPassword(data: {
  password: string;
  token: string;
}): Promise<AxiosResponse> {
  return client.post("api/password-reset/confirm/", data);
}

export function getUser(): Promise<AxiosResponse<User>> {
  return client.get("api/me");
}

export function resendEmail(): Promise<AxiosResponse<ResendEmail>> {
  return client.get("api/user-profile/resend-email/");
}

export const getNotes = async (endpoint: string, params: {}): Promise<Note> => {
  const url = `/api/${endpoint}`;
  const response = await client.get<Note>(url, { params });
  return response.data;
};

export const getMessages = async (params: {
  note_id: string;
  offset?: number;
  limit?: number;
}) => {
  const url = `/api/copilot/note-messages/`;
  const response = await client.get(url, { params });
  return response.data;
};

export const getNoteById = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/`;
  const response = await client.get<INote>(url);
  return response.data;
};

export const getUndoRedoStatus = async (
  noteId: string
): Promise<{
  [key: string]: boolean;
}> => {
  const url = `/api/notes/${noteId}/undo-redo-status/`;
  const response = await client.get<{
    [key: string]: boolean;
  }>(url);
  return response.data;
};

export const undoRedo = async (
  noteId: string,
  status: string
): Promise<{
  [key: string]: boolean;
}> => {
  const url = `/api/notes/${noteId}/undo-redo/?op=${status}`;
  const response = await client.get<{
    [key: string]: boolean;
  }>(url);
  return response.data;
};

export const postMessage = async (
  noteId: string,
  data: object
): Promise<INote> => {
  const url = `/api/copilot/message/?note_id=${noteId}`;
  try {
    const response = await client.post<INote>(url, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const retryMessage = async (
  msgId: string,
  data: object
): Promise<INote> => {
  const url = `/api/copilot/${msgId}/retry/`;
  try {
    const response = await client.post(url, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const clearMessages = async (noteId: string): Promise<INote> => {
  const url = `/api/notes/${noteId}/clear-chat/`;
  try {
    const response = await client.patch(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signNoteById = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/sign/`;
  const response = await client.get<INote>(url);
  return response.data;
};

export const unsignNote = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/unsign/`;
  const response = await client.get<INote>(url);
  return response.data;
};

export const sectionConfigByNoteId = async (
  noteId: string
): Promise<{
  [key: string]: [];
}> => {
  const url = `/api/notes/${noteId}/section-config/`;
  const response = await client.get<{
    [key: string]: [];
  }>(url);
  return response.data;
};

export const getArchivedNoteById = async (
  endpoint: string,
  noteId: string,
  single_endpoint: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/${single_endpoint}`;
  const response = await client.get<INote>(url);
  return response.data;
};

export const updateNoteFieldById = async (
  endpoint: string,
  noteId: string,
  data: object
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/`;
  try {
    const response = await client.patch<INote>(url, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateConfigByNoteId = async (
  noteId: string,
  data: object
): Promise<ISectionConfig> => {
  const url = `/api/notes/${noteId}/update-section-config/`;
  try {
    const response = await client.patch<ISectionConfig>(url, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const archiveNote = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/archive/`;
  try {
    const response = await client.patch<INote>(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const unarchiveNote = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}/unarchive/`;
  try {
    const response = await client.patch<INote>(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteNoteByID = async (
  endpoint: string,
  noteId: string
): Promise<INote> => {
  const url = `/api/${endpoint}/${noteId}`;
  const response = await client.delete<INote>(url);
  return response.data;
};

export function requestUploadImageUrl(data?: User): Promise<AxiosResponse> {
  return client.post("api/image-url/", data, {
    // headers: { authorization: false },
  });
}

export function requestUploadAudioUrl(data?: User): Promise<AxiosResponse> {
  return client.post("api/copilot/presigned-url/", data, {});
}

export function audioProcessed(msgId: string): Promise<Array<INote>> {
  return client.post(`api/copilot/${msgId}/audio-processing/`);
}

export const sendImageToS3 = async (
  url: string,
  params: {
    AWSAccessKeyId: string;
    key: string;
    policy: string;
    signature: string;
  },
  fileToSave: any
) => {
  let res;
  const fd = new FormData();
  fd.append("Content-Type", fileToSave.type);
  fd.append("Cache-Control", "max-age=31536000");
  fd.append("AWSAccessKeyId", params.AWSAccessKeyId);
  fd.append("key", params.key);
  fd.append("policy", params.policy);
  fd.append("signature", params.signature);
  fd.append("file", fileToSave);
  res = await client.post(url, fd, {
    headers: {
      "Content-Type": fileToSave.type,
      authorization: false,
    },
  });
  return res.status;
};

export const sendAudioToS3 = async (
  url: string,
  params: {
    AWSAccessKeyId: string;
    key: string;
    policy: string;
    signature: string;
  },
  fileToSave: any,
  type: string
) => {
  let res;
  const fd = new FormData();
  fd.append("Content-Type", type);
  fd.append("Cache-Control", "max-age=31536000");
  fd.append("AWSAccessKeyId", params.AWSAccessKeyId);
  fd.append("key", params.key);
  fd.append("policy", params.policy);
  fd.append("signature", params.signature);
  fd.append("file", fileToSave);
  res = await client.post(url, fd, {
    headers: {
      "Content-Type": type,
      authorization: false,
    },
  });
  return res.status;
};

export const getTemplates = async (params: {}): Promise<Template> => {
  const url = `/api/note-templates/`;
  const response = await client.get<Template>(url, { params });
  return response.data;
};

export const deleteTemplate = async (
  templateId: string,
  admin_dashboard?: boolean
): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}`;
  const response = await client.delete<ITemplate>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const resetTemplate = async (templateId: string): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}/reset-to-default/`;
  const response = await client.patch<ITemplate>(url);
  return response.data;
};

export function createNewTemplate(data: {
  title: string;
  content: string;
  is_active: boolean;
  category: string;
}): Promise<AxiosResponse> {
  return client.post(`api/note-templates/`, data);
}

export const getTemplate = async (
  templateId: string,
  admin_dashboard?: boolean
): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}/`;
  const response = await client.get<ITemplate>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const updateTemplate = async (
  templateId: string,
  data: object,
  admin_dashboard?: boolean
): Promise<ITemplateVersion> => {
  const url = `/api/note-templates/${templateId}/create-update-draft/`;
  try {
    const response = await client.post<ITemplateVersion>(url, data, {
      params: { admin_dashboard },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const publishTemplate = async (
  templateId: string,
  version: string,
  admin_dashboard?: boolean
): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}/publish/${version}/`;
  try {
    const response = await client.patch<ITemplate>(
      url,
      {},
      {
        params: { admin_dashboard },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDraft = async (
  templateId: string,
  data: object,
  admin_dashboard?: boolean
): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}/create-draft/`;
  try {
    const response = await client.post<ITemplate>(url, data, {
      params: { admin_dashboard },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTemplateVersions = async (
  templateId: string,
  params: {}
): Promise<TemplateVersion> => {
  const url = `/api/note-templates/${templateId}/versions/`;
  const response = await client.get<TemplateVersion>(url, {
    params,
  });
  return response.data;
};

export const getPublishedVersion = async (
  templateId: string,
  admin_dashboard?: boolean
): Promise<ITemplateVersion> => {
  const url = `/api/note-templates/${templateId}/published-version/`;
  const response = await client.get<ITemplateVersion>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const markInactive = async (
  templateId: string,
  admin_dashboard?: boolean
): Promise<ITemplate> => {
  const url = `/api/note-templates/${templateId}/mark-inactive/`;
  const response = await client.patch<ITemplate>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const shareTemplate = async (
  templateId: number,
  data: { emails: string[]; permission_type: string }
): Promise<ShareTemplate> => {
  const url = `/api/share-templates/`;
  const response = await client.post<ShareTemplate>(url, {
    template_id: templateId,
    ...data,
  });
  return response.data;
};

export const getAdminTemplates = async (params: {}): Promise<Template> => {
  const url = `/api/note-templates/list-main-default/`;
  const response = await client.get<Template>(url, { params });
  return response.data;
};

export function createNewAdminTemplate(
  data: {
    title: string;
    content: string;
    is_active: boolean;
    category: string;
  },
  admin_dashboard?: boolean
): Promise<AxiosResponse> {
  return client.post(`api/note-templates/admin/`, data, {
    params: { admin_dashboard },
  });
}

export const getNotifications =
  async (params: {}): Promise<NotificationRes> => {
    const url = `/api/notifications/`;
    const response = await client.get<NotificationRes>(url, { params });
    return response.data;
  };

export const getUnreadNotificationsCount = async (params: {}): Promise<{
  unread_count: number;
}> => {
  const url = `/api/notifications/unread-count/`;
  const response = await client.get<{ unread_count: number }>(url, { params });
  return response.data;
};

export const getShareTemplatesWithMe =
  async (params: {}): Promise<SharedTemplate> => {
    const url = `/api/share-templates/with-me/`;
    const response = await client.get<SharedTemplate>(url, { params });
    return response.data;
  };

export const getSharedWithTemplates = async (params: {}): Promise<{
  shared_with: string[];
  permission_type: string;
}> => {
  const url = `/api/share-templates/shared-with/`;
  const response = await client.get<{
    shared_with: string[];
    permission_type: string;
  }>(url, { params });
  return response.data;
};

export const getSharedTemplateDetailView = async (
  key: string
): Promise<ITemplate> => {
  const url = `/api/share-templates/detail-view/${key}/`;
  const response = await client.get<ITemplate>(url);
  return response.data;
};

export const respondToSharedTemplate = async (
  template_id: string
): Promise<ITemplate> => {
  const url = `/api/share-templates/respond/?template_id=${template_id}`;
  const response = await client.patch<ITemplate>(url);
  return response.data;
};

export const getSharedTemplateDetailViewPublic = async (
  key: string
): Promise<ITemplate> => {
  const url = `/api/share-templates/public/${key}/`;
  const response = await client.get<ITemplate>(url);
  return response.data;
};

export const markAllNotificationsAsRead = async (): Promise<void> => {
  const url = `/api/notifications/mark-all-read/`;
  const response = await client.post<void>(url);
  return response.data;
};

export const setPermissionType = async (
  templateId: string,
  permissionType: string
): Promise<string> => {
  const url = `/api/note-templates/${templateId}/permission-type/`;
  const response = await client.patch<string>(url, {
    permission_type: permissionType,
  });
  return response.data;
};

export const markNotificationAsRead = async (
  notificationSlug: number
): Promise<void> => {
  const url = `/api/notifications/mark-read/`;
  const response = await client.post<void>(url, {
    slug: notificationSlug,
  });
  return response.data;
};

export const updateVersion = async (
  versionId: string,
  data: object,
  admin_dashboard?: boolean
): Promise<ITemplateVersion> => {
  const url = `/api/note-templates/update-version/${versionId}/`;
  const response = await client.patch<ITemplateVersion>(url, data, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const getTemplateVersion = async (
  versionId: string,
  admin_dashboard?: boolean
): Promise<ITemplateVersion> => {
  const url = `/api/note-templates/version/${versionId}/`;
  const response = await client.get<ITemplateVersion>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const createVersion = async (
  templateId: string,
  data: object,
  admin_dashboard?: boolean
): Promise<ITemplateVersion> => {
  const url = `/api/note-templates/${templateId}/create-version/`;
  const response = await client.post<ITemplateVersion>(url, data, {
    params: { admin_dashboard },
  });
  return response.data;
};

export const deleteVersion = async (
  versionId: string,
  admin_dashboard?: boolean
): Promise<void> => {
  const url = `/api/note-templates/delete-version/${versionId}/`;
  const response = await client.delete<void>(url, {
    params: { admin_dashboard },
  });
  return response.data;
};
